import { Button, Form, Input, Modal, Switch, message, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { EditOutlined, CopyOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";

const EXCHANGE_KEYS = ["import_products", "export_products", "import_orders", "export_orders"];

/** Переключатель, жёстко привязанный к полю формы (useWatch + setFieldValue), чтобы значение всегда сохранялось и счётчик «Режим обмена» обновлялся */
function FormBoundSwitch({ form, name, ...rest }) {
  const value = Form.useWatch(name, form);
  return (
    <Switch
      {...rest}
      checked={!!value}
      onChange={(checked) => form.setFieldValue(name, checked)}
    />
  );
}

function exchangeFlagsToLevel(values) {
  return EXCHANGE_KEYS.filter((k) => !!values?.[k]).length;
}

function exchangeLevelToFlags(level) {
  return {
    import_products: level >= 1,
    export_products: level >= 2,
    import_orders: level >= 3,
    export_orders: level >= 4,
  };
}

function CommerceMLModalUpdate({ record, onSave, token }) {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  // Нормализация булевых полей из API (могут прийти true/false, 1/0, "true"/"false"; бэкенд может отдавать active или is_active)
  const normalizeBool = (v) => v === true || v === 1 || v === "true" || v === "1";

  // При открытии модалки подставляем в форму актуальные данные из таблицы (та же запись)
  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        ...record,
        active: normalizeBool(record.active ?? record.is_active),
        import_products: normalizeBool(record.import_products),
        export_products: normalizeBool(record.export_products),
        import_orders: normalizeBool(record.import_orders),
        export_orders: normalizeBool(record.export_orders),
      });
    }
  }, [visible, record, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Берём текущие значения переключателей из формы (в values они могут не попасть при submit)
      const activeVal = !!form.getFieldValue("active");
      const payload = {
        name: values.name,
        url: values.url ?? "",
        username: values.username,
        password: values.password ?? "",
        active: activeVal,
        is_active: activeVal,
        import_products: !!form.getFieldValue("import_products"),
        export_products: !!form.getFieldValue("export_products"),
        import_orders: !!form.getFieldValue("import_orders"),
        export_orders: !!form.getFieldValue("export_orders"),
      };
      await apiRequests.commerceml.updateConnection(record.id, payload, token);
      // Для таблицы берём текущую запись + сохранённые значения (не зависим от ответа API — так переключатели в таблице сразу совпадают с формой)
      const updatedRecord = { ...record, ...payload, id: record.id, active: activeVal };
      message.success("Подключение обновлено");
      setVisible(false);
      onSave?.(updatedRecord);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Ошибка обновления подключения";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const setExchangeLevel = (level) => {
    form.setFieldsValue(exchangeLevelToFlags(level));
  };

  // Отслеживаем изменения всех полей обмена для синхронизации с "Режим обмена"
  const importProducts = Form.useWatch("import_products", form);
  const exportProducts = Form.useWatch("export_products", form);
  const importOrders = Form.useWatch("import_orders", form);
  const exportOrders = Form.useWatch("export_orders", form);

  // Счётчик: сколько из 4 режимов обмена включено (при открытии модалки берём из record, потом из формы)
  const currentLevel = exchangeFlagsToLevel({
    import_products: importProducts ?? (record && normalizeBool(record.import_products)),
    export_products: exportProducts ?? (record && normalizeBool(record.export_products)),
    import_orders: importOrders ?? (record && normalizeBool(record.import_orders)),
    export_orders: exportOrders ?? (record && normalizeBool(record.export_orders)),
  });

  const getConnectionUrl = (connectionId) => {
    const baseUrl = process.env.REACT_APP_APP_URL || window.location.host;
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
    return `${protocol}://${baseUrl}/api/v1/commerceml/${connectionId}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Скопировано в буфер обмена");
  };

  return (
    <>
      <Button
        icon={<EditOutlined />}
        onClick={() => setVisible(true)}
      />
      <Modal
        title={record?.name ? `Редактировать: ${record.name}` : "Редактировать CommerceML подключение"}
        open={visible}
        onCancel={() => {
          setVisible(false);
          form.resetFields();
        }}
        afterOpenChange={(open) => {
          if (open && record) {
            form.setFieldsValue({
              name: record.name,
              url: record.url ?? "",
              username: record.username,
              password: record.password ?? "",
              active: normalizeBool(record.active ?? record.is_active),
              import_products: normalizeBool(record.import_products),
              export_products: normalizeBool(record.export_products),
              import_orders: normalizeBool(record.import_orders),
              export_orders: normalizeBool(record.export_orders),
            });
          }
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ marginBottom: 16, padding: "10px 12px", background: "#f0f7ff", borderRadius: 8, fontSize: 13, color: "#333" }}>
            <strong>Подключение к нашей системе:</strong> внешняя система (1С и т.д.) подключается к нам по URL, логину и паролю. У нас забирают заказы, нам отгружают товары.
          </div>

          <Form.Item
            label="Название"
            name="name"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input placeholder="Например: Подключение к 1С" />
          </Form.Item>

          {record?.id && (
            <Form.Item label="URL для подключения (даёте внешней системе)">
              <Tooltip title="Нажмите, чтобы скопировать">
                <Input
                  value={getConnectionUrl(record.id)}
                  readOnly
                  addonAfter={
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(getConnectionUrl(record.id))}
                      size="small"
                    />
                  }
                  style={{ cursor: "pointer" }}
                  onClick={() => copyToClipboard(getConnectionUrl(record.id))}
                />
              </Tooltip>
            </Form.Item>
          )}

          <Form.Item
            label="URL внешней системы (опционально)"
            name="url"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || !String(value).trim()) return Promise.resolve();
                  try {
                    new URL(value);
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error("Введите корректный URL"));
                  }
                },
              },
            ]}
            tooltip="Если наша система инициирует запросы к внешней системе — укажите их URL. Иначе можно оставить пустым."
          >
            <Input placeholder="https://example.com/commerceml" allowClear />
          </Form.Item>

          <Form.Item
            label="Логин"
            name="username"
            rules={[{ required: true, message: "Введите логин" }]}
          >
            <Input placeholder="Логин для доступа внешней системы к нам" />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[{ required: true, message: "Введите пароль" }]}
          >
            <Input.Password placeholder="Пароль для доступа внешней системы к нам" />
          </Form.Item>

          <Form.Item label="Настройки обмена" style={{ marginBottom: 0 }}>
            <Form.Item name="active" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="active" checkedChildren="Активно" unCheckedChildren="Неактивно" />
              <span style={{ marginLeft: 8 }}>Активно</span>
            </Form.Item>

            <Form.Item label="Режим обмена" style={{ marginBottom: 16 }} tooltip="Сколько из четырёх режимов обмена включено: Принимать товары, Выгружать товары, Принимать заказы, Выгружать заказы">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  type="button"
                  size="small"
                  disabled={currentLevel <= 0}
                  onClick={() => setExchangeLevel(Math.max(0, currentLevel - 1))}
                >
                  −
                </Button>
                <span style={{ minWidth: 56, fontWeight: 500 }}>{currentLevel} из 4</span>
                <Button
                  type="button"
                  size="small"
                  disabled={currentLevel >= 4}
                  onClick={() => setExchangeLevel(Math.min(4, currentLevel + 1))}
                >
                  +
                </Button>
              </span>
            </Form.Item>

            <Form.Item name="import_products" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="import_products" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Принимать товары</span>
            </Form.Item>

            <Form.Item name="export_products" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="export_products" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Выгружать товары</span>
            </Form.Item>

            <Form.Item name="import_orders" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="import_orders" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Принимать заказы</span>
            </Form.Item>

            <Form.Item name="export_orders" style={{ marginBottom: 16 }}>
              <FormBoundSwitch form={form} name="export_orders" checkedChildren="Вкл" unCheckedChildren="Выкл" />
              <span style={{ marginLeft: 8 }}>Выгружать заказы</span>
            </Form.Item>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default CommerceMLModalUpdate;
